import {
  action,
  computed,
  makeObservable,
  observable,
  runInAction,
} from 'mobx';

import { ENDPOINTS } from 'config/endpoints';
import { FormHeaderEntity } from 'entities/FormHeader';
import { normalizeTableColumnEntity } from 'entities/TableColumn';
import {
  TableColumnRequestApi,
  TableColumnsResponseApi,
} from 'entities/endpoints/form.table_header';
import { ApiRequest } from 'stores/common/ApiRequest';
import { ILocalStore } from 'stores/interfaces';

import { ITableFactoryStore } from '../TableFactoryStore/interface';

import { TableColumnModel, ITableColumnModel } from './TableColumnModel';

type PrivateFields = '_columnModels' | '_header';

type TableFactoryHeaderStoreParams = {
  factoryStore: ITableFactoryStore;
};

/** Стор заголовков универсальной таблицы, который подгружает данные по заголовкам таблицы */
class TableFactoryHeaderStore implements ILocalStore {
  constructor(params: TableFactoryHeaderStoreParams) {
    this.initRequest = new ApiRequest({
      methodType: 'GET',
      endpoint: ENDPOINTS.form.tableHeader,
    });

    this._factoryStore = params.factoryStore;

    makeObservable<TableFactoryHeaderStore, PrivateFields>(this, {
      _columnModels: observable.ref,
      columns: computed,
      columnModels: computed,

      _header: observable.ref,
      header: computed,

      init: action.bound,
    });
  }

  readonly initRequest: ApiRequest;

  private _columnModels: Map<string, ITableColumnModel> = new Map();

  private _header: FormHeaderEntity | null = null;

  private readonly _factoryStore: ITableFactoryStore;

  get columns(): ITableColumnModel[] {
    return Array.from(this._columnModels.values());
  }

  get columnModels(): Map<string, ITableColumnModel> {
    return this._columnModels;
  }

  get header(): FormHeaderEntity | null {
    return this._header;
  }

  async init(params: TableColumnRequestApi): Promise<void> {
    const { isError, data } = await this.initRequest.call<
      TableColumnsResponseApi,
      TableColumnRequestApi
    >({
      data: params,
    });

    if (isError || !data) {
      return;
    }

    runInAction(() => {
      this._destroyColumnModels();
      this._columnModels = data.columns.reduce((acc, curr) => {
        acc.set(
          curr.id,
          new TableColumnModel({
            entity: normalizeTableColumnEntity(curr),
            factoryStore: this._factoryStore,
          })
        );

        return acc;
      }, new Map() as Map<string, ITableColumnModel>);

      this._header = data.header;
    });
  }

  private _destroyColumnModels(): void {
    Object.values(this._columnModels).forEach((model) => model.destroy());
  }

  destroy(): void {
    this.initRequest.destroy();
    this._destroyColumnModels();
  }
}

export default TableFactoryHeaderStore;
